import React, { useEffect, useMemo } from 'react';
import {
  Navigate,
  RouteObject,
  useLocation,
  useRoutes,
} from 'react-router-dom';
import { Loader } from './common/components/Loader';
import { APP_PATH_PREFIX } from './common/constants/common';
import { useGetQueryParam, useQueryParams } from './common/hooks';
import {
  PARTNER_STACK_ID_QUERY_STRING_KEY,
  PARTNER_STACK_PARTNER_KEY_QUERY_STRING_KEY,
  PartnerStackService,
} from './common/partnerStackHelper';
import { useSetupYupLocale } from './common/useSetupYupLocale';
import { UtmService } from './common/utmHelper';
import { SocialAuthHelper } from './components/social-auth-helper';
import { JoinApp } from './JoinApp';

const LazyAuthApp = React.lazy(() => import('./components/auth/app'));
const LazySharedApp = React.lazy(() => import('./pages/SharedApp'));
const LazyMainApp = React.lazy(() => import('./components/main/app'));
// const LazyBookkeepingApp = React.lazy(() => import('./pages/BookkeepingApp'));
// const LazyAccountantApp = React.lazy(() => import('./pages/AccountantApp'));
const LazyPracticeManagementApp = React.lazy(() => import('./pages/PmApp'));
const LazyPmClientApp = React.lazy(() => import('./components/pm-client/app'));
// const LazyProposalApp = React.lazy(() => import('./components/proposal/app'));

const App = () => {
  const { search } = useLocation();

  const psId = useGetQueryParam(PARTNER_STACK_ID_QUERY_STRING_KEY);
  const psPartnerKey = useGetQueryParam(
    PARTNER_STACK_PARTNER_KEY_QUERY_STRING_KEY,
  );

  const queryParams = useQueryParams();

  const utmData = useMemo(() => {
    const data: Record<string, string> = {};

    queryParams.forEach((value, key) => {
      if (key.startsWith('utm_')) {
        data[key] = value;
      }
    });

    return data;
  }, [queryParams]);

  useEffect(() => {
    PartnerStackService.identify(psId, psPartnerKey);
  }, [psId, psPartnerKey]);

  useEffect(() => {
    UtmService.setToStorage(utmData);
  }, [utmData]);

  useSetupYupLocale();

  const joinRoute = useMemo(
    () => ({
      path: '/join',
      element: <JoinApp />,
    }),
    [],
  );

  const authRoutes: RouteObject = useMemo(
    () => ({
      path: '/auth/*',
      element: <LazyAuthApp />,
    }),
    [],
  );

  const sharedRoutes: RouteObject = useMemo(
    () => ({
      path: '/shared/*',
      element: <LazySharedApp />,
    }),
    [],
  );

  // const privateRoutes: RouteObject = useMemo(
  //   () => ({
  //     path: '/bookkeeping/*',
  //     element: <LazyBookkeepingApp />,
  //   }),
  //   [],
  // );

  // const accountantRoutes: RouteObject = useMemo(
  //   () => ({
  //     path: '/accountant/*',
  //     element: <LazyAccountantApp />,
  //   }),
  //   [],
  // );

  const practiceManagementRoutes: RouteObject = useMemo(
    () => ({
      path: `${APP_PATH_PREFIX.PM}/*`,
      element: <LazyPracticeManagementApp />,
    }),
    [],
  );

  const pmClientRoutes: RouteObject = useMemo(
    () => ({
      path: `${APP_PATH_PREFIX.PM_CLIENT}/*`,
      element: <LazyPmClientApp />,
    }),
    [],
  );

  // const proposalRoutes: RouteObject = useMemo(
  //   () => ({
  //     path: `${APP_PATH_PREFIX.P2P}/*`,
  //     element: <LazyProposalApp />,
  //   }),
  //   [],
  // );

  const mainAppRoutes: RouteObject = useMemo(
    () => ({
      path: '/app/*',
      element: <LazyMainApp />,
    }),
    [],
  );

  const socialAuthHelperRoute: RouteObject = useMemo(
    () => ({
      path: '/social-auth-helper',
      element: <SocialAuthHelper />,
    }),
    [],
  );

  const defaultRoute: RouteObject = useMemo(
    () => ({
      path: '*',
      element: <Navigate to={`/app${search}`} />,
    }),
    [search],
  );

  const routing = useRoutes([
    defaultRoute,
    mainAppRoutes,
    joinRoute,
    authRoutes,
    sharedRoutes,
    // accountantRoutes,
    // privateRoutes,
    practiceManagementRoutes,
    pmClientRoutes,
    // proposalRoutes,
    socialAuthHelperRoute,
  ]);

  return <React.Suspense fallback={<Loader />}>{routing}</React.Suspense>;
};

export default App;
